// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from '@tanstack/react-query';
import { useHttpClient } from '@toggl/sync/http';
import type {
  JobopeningGenerateDescriptionPayload,
  JobopeningPatchOpeningDescription,
} from '../types';
import type { MutationOptions } from '../utils/mutations';
import {
  generateJobDescriptionMutationKey,
  patchJobopeningDescriptionMutationKey,
} from './keys';
import { generateJobDescription, patchJobopeningDescription } from './requests';

export type PatchJobopeningDescriptionMutationVariables = {
  workspaceId: number;
  openingId: number;
  data: Partial<JobopeningPatchOpeningDescription>;
};

export type PatchJobopeningDescriptionMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof patchJobopeningDescription,
    PatchJobopeningDescriptionMutationVariables,
    TContext
  >;

/**
 * Patch job opening description
 */
export function usePatchJobopeningDescriptionMutation<TContext = unknown>(
  options: PatchJobopeningDescriptionMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, data }) =>
      patchJobopeningDescription(httpClient, workspaceId, openingId, data),
    mutationKey: patchJobopeningDescriptionMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending patchJobopeningDescription mutation. */
export function usePatchJobopeningDescriptionMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: patchJobopeningDescriptionMutationKey,
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as PatchJobopeningDescriptionMutationVariables,
  });
}

export type GenerateJobDescriptionMutationVariables = {
  workspaceId: number;
  openingId: number;
  data: JobopeningGenerateDescriptionPayload;
};

export type GenerateJobDescriptionMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof generateJobDescription,
    GenerateJobDescriptionMutationVariables,
    TContext
  >;

/**
 * Given a job title, a list of required skills, a seniority level, and a team size, returns a detailed Markdown-formatted job description.
 */
export function useGenerateJobDescriptionMutation<TContext = unknown>(
  options: GenerateJobDescriptionMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspaceId, openingId, data }) =>
      generateJobDescription(httpClient, workspaceId, openingId, data),
    mutationKey: generateJobDescriptionMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending generateJobDescription mutation. */
export function useGenerateJobDescriptionMutationVariables() {
  return useMutationState({
    filters: {
      mutationKey: generateJobDescriptionMutationKey,
      status: 'pending',
    },
    select: (mutation) =>
      mutation.state.variables as GenerateJobDescriptionMutationVariables,
  });
}
